import React, { useEffect, useRef, useState, useCallback } from 'react';
import { connect } from 'react-redux';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  getOrCreateRoom,
  getOrCreateDmRoom,
  getMessages,
  sendMessage,
  markRead,
  subscribeToMessages,
  getRoomMembers,
  addReaction,
  removeReaction,
  deleteMessage,
  editMessage,
  notifyChatMembers,
} from '../../services/chat';
import MessageBubble from './MessageBubble';
import ChatInput from './ChatInput';
import MessageActionSheet from './MessageActionSheet';
import { DEFAULT_THEME_COLOR } from '../../constants/theme';
import { BOTTOM_NAVIGATION_VISIBILITY } from '../../constants/actionTypes';

const ChatRoomView = ({ currentUser, setBottomNavigationVisible }) => {
  const { roomId: roomIdParam, dmUsername: dmUsernameParam } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state || {};
  const dmUsername = dmUsernameParam || state.dmUsername;
  const { roomName, entityType, entityId, entityName, participantUsernames } = state;

  const currentUsername = currentUser?.username || '';
  const bottomRef = useRef(null);

  const [roomId, setRoomId] = useState(!dmUsername && roomIdParam ? roomIdParam : null);
  const [resolvedRoomName, setResolvedRoomName] = useState(roomName || entityName || dmUsername || 'Chat');
  const [messages, setMessages] = useState([]);
  const [members, setMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [actionMessage, setActionMessage] = useState(null);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [replyTo, setReplyTo] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    setBottomNavigationVisible(false);
    return () => setBottomNavigationVisible(true);
  }, []);

  useEffect(() => {
    if (!currentUsername) return;
    (async () => {
      try {
        let resolvedId = roomId;

        if (!resolvedId) {
          let room;
          if (dmUsername) {
            room = await getOrCreateDmRoom(currentUsername, dmUsername);
          } else if (entityType && entityId) {
            room = await getOrCreateRoom(entityType, entityId, entityName || 'Chat', currentUsername, participantUsernames || []);
          } else {
            return;
          }
          resolvedId = room.id;
          setRoomId(resolvedId);
          setResolvedRoomName(room.name || dmUsername || 'Chat');
        }

        const [msgs, mems] = await Promise.all([getMessages(resolvedId), getRoomMembers(resolvedId)]);
        setMessages(msgs);
        setMembers(mems);
        setHasMore(msgs.length === 50);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [currentUsername]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (!roomId) return;
    const sub = subscribeToMessages(roomId, (newMsg) => {
      setMessages(prev => {
        if (prev.some(m => m.id === newMsg.id)) return prev;
        return [...prev, newMsg];
      });
    });
    return () => {
      sub.unsubscribe();
      if (roomId) markRead(roomId, currentUsername);
    };
  }, [roomId]);

  const loadMore = useCallback(async () => {
    if (!roomId || isLoadingMore || !hasMore) return;
    const oldest = messages[0];
    if (!oldest) return;
    setIsLoadingMore(true);
    const older = await getMessages(roomId, oldest.created_at);
    setHasMore(older.length === 50);
    setMessages(prev => [...older.filter(m => !prev.some(p => p.id === m.id)), ...prev]);
    setIsLoadingMore(false);
  }, [roomId, isLoadingMore, hasMore, messages]);

  const handleSend = useCallback(async (content, replyToId = null) => {
    if (!roomId) return;
    const tempId = `temp-${Date.now()}`;
    const optimistic = {
      id: tempId,
      room_id: roomId,
      sender_username: currentUsername,
      content,
      created_at: new Date().toISOString(),
      reactions: [],
      reply_to: replyTo
        ? { id: replyTo.id, sender_username: replyTo.sender_username, content: replyTo.content }
        : null,
      edited_at: null,
    };
    setMessages(prev => [...prev, optimistic]);
    setReplyTo(null);
    setIsSending(true);
    try {
      const saved = await sendMessage(roomId, content, currentUsername, replyToId);
      setMessages(prev => prev.map(m =>
        m.id === tempId ? { ...saved, reply_to: optimistic.reply_to, reactions: [] } : m
      ));
      notifyChatMembers(members, currentUsername, content);
      await markRead(roomId, currentUsername);
    } catch {
      setMessages(prev => prev.filter(m => m.id !== tempId));
      setError('Failed to send message.');
    } finally {
      setIsSending(false);
    }
  }, [roomId, currentUsername, members, replyTo]);

  const handleEditSave = useCallback(async (messageId, newContent) => {
    setMessages(prev => prev.map(m =>
      m.id === messageId ? { ...m, content: newContent, edited_at: new Date().toISOString() } : m
    ));
    setEditingMessageId(null);
    try {
      await editMessage(messageId, newContent);
    } catch {
      setError('Failed to edit message.');
    }
  }, []);

  const handleEditCancel = useCallback(() => {
    setEditingMessageId(null);
  }, []);

  const handleDeleteMessage = useCallback(async () => {
    if (!actionMessage) return;
    setMessages(prev => prev.filter(m => m.id !== actionMessage.id));
    try {
      await deleteMessage(actionMessage.id);
    } catch {
      setMessages(prev => [...prev, actionMessage]);
      setError('Failed to delete message.');
    }
    setActionMessage(null);
  }, [actionMessage]);

  const handleReactionSelect = useCallback(async (emoji) => {
    if (!actionMessage || !roomId) return;
    const alreadyReacted = actionMessage.reactions?.some(
      r => r.username === currentUsername && r.emoji === emoji,
    );
    if (alreadyReacted) {
      await removeReaction(actionMessage.id, emoji, currentUsername);
    } else {
      await addReaction(actionMessage.id, emoji, currentUsername);
    }
    const refreshed = await getMessages(roomId);
    setMessages(refreshed);
    setActionMessage(null);
  }, [actionMessage, roomId, currentUsername]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100dvh',
        overflow: 'hidden',
        background: 'linear-gradient(135deg, #0d1b2a 0%, #091420 100%)',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1,
          pt: 'calc(8px + env(safe-area-inset-top))',
          pb: 1,
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          backgroundColor: 'rgba(13,27,42,0.95)',
          flexShrink: 0,
        }}
      >
        <IconButton onClick={() => navigate('/chat')} sx={{ color: 'white' }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" sx={{ color: 'white', fontWeight: 600, fontSize: '1rem' }}>
          {resolvedRoomName}
        </Typography>
      </Box>

      {/* Messages */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6, flex: 1 }}>
          <CircularProgress sx={{ color: DEFAULT_THEME_COLOR }} />
        </Box>
      ) : (
        <Box sx={{ flex: 1, overflowY: 'auto', py: 1 }}>
          {hasMore && (
            <Typography
              onClick={loadMore}
              sx={{ color: DEFAULT_THEME_COLOR, textAlign: 'center', cursor: 'pointer', py: 1.5, fontSize: '0.8rem' }}
            >
              {isLoadingMore ? 'Loading…' : 'Load earlier messages'}
            </Typography>
          )}
          {messages.map(msg => (
            <MessageBubble
              key={msg.id}
              message={msg}
              isOwn={msg.sender_username === currentUsername}
              onAction={(m) => setActionMessage(m)}
              isEditing={editingMessageId === msg.id}
              onEditSave={(newContent) => handleEditSave(msg.id, newContent)}
              onEditCancel={handleEditCancel}
            />
          ))}
          <div ref={bottomRef} />
        </Box>
      )}

      {error && (
        <Typography variant="caption" sx={{ color: '#ef4444', px: 2, py: 0.5 }}>
          {error}
        </Typography>
      )}

      <ChatInput
        onSend={handleSend}
        disabled={isSending}
        replyTo={replyTo}
        onCancelReply={() => setReplyTo(null)}
      />

      <MessageActionSheet
        open={Boolean(actionMessage)}
        isOwn={actionMessage?.sender_username === currentUsername}
        onReact={handleReactionSelect}
        onReply={() => {
          setReplyTo({ id: actionMessage.id, sender_username: actionMessage.sender_username, content: actionMessage.content });
          setActionMessage(null);
        }}
        onEdit={() => {
          setEditingMessageId(actionMessage.id);
          setActionMessage(null);
        }}
        onDelete={handleDeleteMessage}
        onClose={() => setActionMessage(null)}
      />
    </Box>
  );
};

const mapStateToProps = state => ({
  currentUser: state.common.currentUser,
});

const mapDispatchToProps = dispatch => ({
  setBottomNavigationVisible: isVisible => dispatch({ type: BOTTOM_NAVIGATION_VISIBILITY, payload: isVisible }),
});

export default connect(mapStateToProps, mapDispatchToProps)(ChatRoomView);
